import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = "March 17, 2026";

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-slate-700 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-emerald-400" />
            <span className="text-lg font-bold text-slate-100">Trading Journal</span>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-slate-100 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-slate-300 leading-relaxed">
          {/* Introduction */}
          <section>
            <p>
              Trading Journal ("we," "our," or "us") operates the Trading Journal web and mobile
              applications (the "Service"). This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our Service.
            </p>
            <p className="mt-3">
              By using the Service, you agree to the collection and use of information in
              accordance with this policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          {/* 1 - Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">1. Information We Collect</h2>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">1.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Account information:</strong> name, email address, and password (hashed with bcrypt)</li>
              <li><strong>Google OAuth data:</strong> name, email, and Google account identifier when you sign in with Google</li>
              <li><strong>Broker account credentials:</strong> API keys and OAuth tokens for connected brokerage accounts (encrypted with AES-256)</li>
              <li><strong>Journal entries:</strong> trade notes, tags, and personal reflections you choose to record</li>
              <li><strong>User preferences:</strong> timezone, currency, and display settings</li>
              <li><strong>AI configuration:</strong> third-party LLM API keys you optionally provide (encrypted with AES-256-GCM)</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">1.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Trade data:</strong> trade history, positions, executions, and performance metrics synced from connected broker accounts</li>
              <li><strong>Usage data:</strong> pages visited, features used, and interaction patterns</li>
              <li><strong>Device information:</strong> browser type, operating system, and device identifiers</li>
              <li><strong>Log data:</strong> IP addresses, access times, and error logs</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">1.3 Information We Do NOT Collect</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Social Security numbers or government-issued IDs</li>
              <li>Bank account numbers or routing numbers</li>
              <li>Credit card numbers (payments are handled by Stripe)</li>
              <li>Physical addresses or phone numbers</li>
            </ul>
          </section>

          {/* 2 - How We Use Your Information */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">2. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Provide, operate, and maintain the Service</li>
              <li>Sync and display your trade data from connected brokerage accounts</li>
              <li>Calculate analytics, performance metrics, and trading statistics</li>
              <li>Generate AI-powered analysis when you opt in and provide your own API keys</li>
              <li>Authenticate your identity and secure your account</li>
              <li>Send essential service communications (password resets, security alerts)</li>
              <li>Improve and personalize the Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* 3 - Data Sharing and Third Parties */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">3. Data Sharing and Third Parties</h2>
            <p>We do not sell your personal information. We may share data with:</p>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">3.1 Service Providers</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Hosting:</strong> Render.com (application hosting and database)</li>
              <li><strong>Payments:</strong> Stripe (subscription billing &mdash; we never see your card details)</li>
              <li><strong>Authentication:</strong> Google (OAuth sign-in)</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">3.2 AI Analysis Providers (User-Initiated)</h3>
            <p>
              When you enable AI features and provide your own API key, aggregated trade
              statistics (symbols, win rates, P&L summaries) may be sent to the LLM provider
              you selected (OpenAI, Anthropic, or Google). This data is sent directly using
              <strong> your</strong> API key and is subject to that provider's privacy policy.
              We do not send data to AI providers without your explicit action.
            </p>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">3.3 Brokerage APIs</h3>
            <p>
              We connect to broker APIs (Alpaca, Charles Schwab) solely to sync your trade
              data. We use read-only access where possible and never execute trades on your behalf.
            </p>

            <h3 className="text-lg font-medium text-slate-200 mt-4 mb-2">3.4 Legal Requirements</h3>
            <p>
              We may disclose information if required by law, court order, or governmental
              request, or to protect our rights, safety, or property.
            </p>
          </section>

          {/* 4 - Data Security */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">4. Data Security</h2>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>AES-256 encryption for stored broker credentials and API keys</li>
              <li>bcrypt password hashing with 12 salt rounds</li>
              <li>JWT-based authentication with configurable expiration</li>
              <li>Rate limiting on authentication endpoints</li>
              <li>Optional two-factor authentication (TOTP)</li>
            </ul>
            <p className="mt-3">
              While we strive to protect your data, no method of electronic storage or
              transmission is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* 5 - Data Retention */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">5. Data Retention</h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide
              the Service. When you delete your account, we will delete or anonymize your
              personal data within 30 days, except where we are required to retain it for legal
              or regulatory purposes.
            </p>
            <p className="mt-3">
              Trade data synced from brokers is stored to provide analytics and journaling
              features. You may request deletion of this data at any time.
            </p>
          </section>

          {/* 6 - Your Rights */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">6. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Export your data in a machine-readable format (CSV/JSON)</li>
              <li><strong>Opt-out:</strong> Withdraw consent for optional data processing</li>
              <li><strong>Restrict processing:</strong> Limit how we use your data</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at the email address below.
            </p>
          </section>

          {/* 7 - California Residents (CCPA) */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">7. California Residents (CCPA)</h2>
            <p>If you are a California resident, you have additional rights under the CCPA:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Right to know what personal information we collect, use, and disclose</li>
              <li>Right to request deletion of your personal information</li>
              <li>Right to opt out of the sale of personal information (we do not sell your data)</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </ul>
          </section>

          {/* 8 - European Residents (GDPR) */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">8. European Residents (GDPR)</h2>
            <p>If you are in the European Economic Area (EEA), our legal bases for processing are:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Contract:</strong> Processing necessary to provide the Service you requested</li>
              <li><strong>Consent:</strong> For optional features such as AI analysis and broker integration</li>
              <li><strong>Legitimate interest:</strong> For security, fraud prevention, and service improvement</li>
            </ul>
            <p className="mt-3">
              You may withdraw consent at any time. You also have the right to lodge a complaint
              with your local data protection authority.
            </p>
          </section>

          {/* 9 - Children */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">9. Children's Privacy</h2>
            <p>
              The Service is not intended for individuals under the age of 18. We do not
              knowingly collect personal information from children. If we become aware that a
              child under 18 has provided us with personal data, we will take steps to delete
              that information.
            </p>
          </section>

          {/* 10 - Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">10. Cookies and Tracking</h2>
            <p>
              We use essential cookies and local storage for authentication and session
              management. We do not use third-party tracking cookies or advertising cookies.
            </p>
          </section>

          {/* 11 - Changes */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of
              material changes by posting the updated policy on this page and updating the
              "Last updated" date. Continued use of the Service after changes constitutes
              acceptance of the updated policy.
            </p>
          </section>

          {/* 12 - Contact */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your data
              rights, please contact us at:
            </p>
            <p className="mt-2 text-emerald-400">privacy@rmultiverse.com</p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-700 flex flex-wrap gap-6 text-sm text-slate-500">
          <Link to="/terms" className="hover:text-emerald-400 transition-colors">Terms of Service</Link>
          <Link to="/disclaimer" className="hover:text-emerald-400 transition-colors">Disclaimer</Link>
          <Link to="/login" className="hover:text-emerald-400 transition-colors">Login</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
